<template>
  <div
    class="highlighting-settings"
    :class="{ 'highlighting-settings--collapsed': isSettingsCollapsed }"
  >
    <div class="highlight-settings-layout">
      <!-- Labels Column -->
      <div class="labels-column">
        <span class="function-label">Show</span>
        <span class="function-label">Color</span>
        <span class="function-label">Bright</span>
      </div>

      <!-- Controls Columns -->
      <div class="controls-columns">
        <div
          v-for="(setting, index) in scaleDegreeSettings"
          :key="`column-${index}`"
          class="note-column"
          @mouseenter="handleColumnEnter(index)"
          @mouseleave="handleColumnLeave(index)"
        >
          <!-- Show/Hide Button -->
          <button
            class="show-button interactive-outline"
            :class="{
              'show-button--visible': setting.show,
              'show-button--hidden': !setting.show,
              'show-button--colored': setting.color,
              'show-button--bright': setting.bright,
            }"
            :style="
              setting.show ? { backgroundColor: getNoteColor(index) } : {}
            "
            @click="toggleShow(index)"
            @mouseenter="
              handleButtonHover(
                index,
                'show',
                setting.show ? 'Hide note' : 'Show note'
              )
            "
          >
            <!-- Use NoteLabel for note mode, ScaleDegreeLabel for scale degree mode -->
            <template v-if="!isScaleDegree">
              <div
                class="button-note-label"
                :style="{
                  color: getTextColor(index),
                  fill: getTextColor(index),
                }"
              >
                <NoteLabel
                  :chromatic-index="(index + noteNames.indexOf(rootNote)) % 12"
                  :display-mode="'logical'"
                  :key-signature="rootNote"
                  size="medium"
                />
              </div>
            </template>
            <template v-else>
              <div
                class="button-note-label"
                :style="{
                  color: getTextColor(index),
                  fill: getTextColor(index),
                }"
              >
                <ScaleDegreeLabel
                  :chromatic-interval="index"
                  :scale="selectedScale"
                  size="medium"
                />
              </div>
            </template>
          </button>

          <!-- Color Button -->
          <button
            class="color-button interactive-outline"
            :class="{
              'color-button--active': setting.color,
              'color-button--inactive': !setting.color,
              'color-button--hidden': !setting.show,
            }"
            :style="{
              fill: getColorButtonColor(index),
            }"
            :tabindex="setting.show ? 0 : -1"
            @click="toggleColor(index)"
            @mouseenter="
              handleButtonHover(
                index,
                'color',
                setting.color ? 'Color off' : 'Color on'
              )
            "
          >
            <svg :viewBox="getSvgPath('colors')?.viewBox">
              <path
                v-for="(path, pathIndex) in getSvgPath('colors')?.paths"
                :key="`color-path-${pathIndex}`"
                :d="path.d"
                :fill-rule="path.fillRule"
              />
            </svg>
          </button>

          <!-- Bright Button -->
          <button
            class="dim-button interactive-outline"
            :class="{
              'dim-button--bright': setting.bright,
              'dim-button--dimmed': !setting.bright,
              'dim-button--hidden': !setting.show,
            }"
            :style="{ fill: getDimColor(index) }"
            :tabindex="setting.show ? 0 : -1"
            @click="toggleBright(index)"
            @mouseenter="
              handleButtonHover(
                index,
                'bright',
                setting.bright ? 'Bright off' : 'Bright on'
              )
            "
          >
            <svg :viewBox="getSvgPath('brightness')?.viewBox">
              <path
                v-for="(path, pathIndex) in getSvgPath('brightness')?.paths"
                :key="`brightness-path-${pathIndex}`"
                :d="path.d"
                :fill-rule="path.fillRule"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Unified Column Tooltip -->
    <ElementTooltip
      :visible="columnTooltip.visible"
      :content="columnTooltip.content"
      :target-element="columnTooltip.targetElement"
      :position="'above'"
    />
  </div>
</template>

<script setup lang="ts">
import { watch, onMounted, onUnmounted, ref } from "vue";
import { useFretboardStore } from "@/stores/fretboard";
import { storeToRefs } from "pinia";
import { getSvgPath } from "@/utils/svgPaths";
import { noteNames } from "@/utils/noteUtils";
import ElementTooltip from "./ElementTooltip.vue";
import NoteLabel from "./NoteLabel.vue";
import ScaleDegreeLabel from "./ScaleDegreeLabel.vue";
import { useGlobalTooltip } from "@/composables/useGlobalTooltip";

// Global tooltip coordination
const {
  registerTooltip,
  unregisterTooltip,
  showTooltip: showGlobalTooltip,
  hideTooltip: hideGlobalTooltip,
} = useGlobalTooltip();

// Column-based tooltip state
const columnTooltip = ref({
  visible: false,
  content: "",
  targetElement: null as HTMLElement | null,
  currentColumn: -1,
});

const store = useFretboardStore();
const {
  scaleDegreeSettings,
  rootNote,
  isScaleDegree,
  selectedScale,
  isSettingsCollapsed,
} = storeToRefs(store);

// Use tooltip functions from props (access directly to maintain reactivity)

function getNoteColor(index: number) {
  const setting = scaleDegreeSettings.value[index];

  if (!setting.color) {
    // Show buttons: bright = shade-60 bg, dim = shade-30 bg
    return setting.bright ? "var(--shade-60)" : "var(--shade-30)";
  }

  // index now represents chromatic interval directly
  const colorIndex = index % 12;
  return setting.bright
    ? `var(--color-${colorIndex})`
    : `var(--color-${colorIndex}-dimmed)`;
}

function getTextColor(index: number) {
  const setting = scaleDegreeSettings.value[index];

  // Show buttons OFF: shade-40
  if (!setting.show) return "var(--shade-40)";

  if (!setting.color) {
    // Show buttons ON + brightness OFF: shade-60 text
    // Show buttons ON + brightness ON: shade-10 text
    return setting.bright ? "var(--shade-10)" : "var(--shade-60)";
  }

  // When color is enabled, use dark text on bright colors, light text on dim colors
  return setting.bright ? "var(--shade-20)" : "var(--light-48)";
}

function getColorButtonColor(index: number) {
  const setting = scaleDegreeSettings.value[index];
  if (!setting.color) {
    return "var(--shade-40)";
  }
  // Always use bright color for color button icons for better contrast
  const colorIndex = index % 12;
  return `var(--color-${colorIndex})`;
}

function getDimColor(index: number) {
  const setting = scaleDegreeSettings.value[index];
  if (!setting.color) {
    // When colors are OFF: bright buttons should be shade-60, dimmed buttons shade-40
    return setting.bright ? "var(--shade-60)" : "var(--shade-40)";
  }
  // When colors are ON: active dim buttons shade-60, inactive dim buttons shade-40
  return setting.bright ? "var(--shade-60)" : "var(--shade-40)";

  // Alternative colored approach (may want to return to this):
  // const colorIndex = index % 12;
  // return setting.bright
  //   ? `var(--color-${colorIndex})`
  //   : `var(--color-${colorIndex}-dimmed)`;
}

function toggleShow(index: number) {
  const currentSettings = [...scaleDegreeSettings.value];
  currentSettings[index] = {
    ...currentSettings[index],
    show: !currentSettings[index].show,
  };
  store.setScaleDegreeSettings(currentSettings);
  store.switchToCustomMode(); // Auto-switch dropdown to Custom (label only)

  // Update tooltip content immediately if this column is active
  if (
    columnTooltip.value.currentColumn === index &&
    columnTooltip.value.visible
  ) {
    columnTooltip.value.content = currentSettings[index].show
      ? "Hide note"
      : "Show note";
  }
}

function toggleColor(index: number) {
  const currentSettings = [...scaleDegreeSettings.value];
  currentSettings[index] = {
    ...currentSettings[index],
    color: !currentSettings[index].color,
  };
  store.setScaleDegreeSettings(currentSettings);

  // Update tooltip content immediately if this column is active
  if (
    columnTooltip.value.currentColumn === index &&
    columnTooltip.value.visible
  ) {
    columnTooltip.value.content = currentSettings[index].color
      ? "Color off"
      : "Color on";
  }
}

function toggleBright(index: number) {
  const currentSettings = [...scaleDegreeSettings.value];
  currentSettings[index] = {
    ...currentSettings[index],
    bright: !currentSettings[index].bright,
  };
  store.setScaleDegreeSettings(currentSettings);

  // Update tooltip content immediately if this column is active
  if (
    columnTooltip.value.currentColumn === index &&
    columnTooltip.value.visible
  ) {
    columnTooltip.value.content = currentSettings[index].bright
      ? "Bright off"
      : "Bright on";
  }
}

watch(
  () => store.isScaleDegree,
  (newValue) => {
    isScaleDegree.value = newValue;
  }
);

// Note: Removed forced re-render - Vue's reactivity handles this automatically

// Column-based tooltip handlers
function handleColumnEnter(columnIndex: number) {
  columnTooltip.value.currentColumn = columnIndex;
}

function handleColumnLeave(columnIndex: number) {
  // Only hide if we're leaving the current column
  if (columnTooltip.value.currentColumn === columnIndex) {
    hideColumnTooltip();
    columnTooltip.value.currentColumn = -1;
  }
}

function handleButtonHover(
  columnIndex: number,
  _buttonType: "show" | "color" | "bright",
  content: string
) {
  // Only show tooltip if we're in the current column
  if (columnTooltip.value.currentColumn === columnIndex) {
    columnTooltip.value.content = content;
    columnTooltip.value.visible = true;
    showGlobalTooltip("highlight-settings");

    // Always position tooltip above the show button (first button in column)
    const showButton = document.querySelector(
      `.note-column:nth-child(${columnIndex + 1}) .show-button`
    ) as HTMLElement;
    if (showButton) {
      columnTooltip.value.targetElement = showButton;
    }
  }
}

function hideColumnTooltip() {
  columnTooltip.value.visible = false;
  hideGlobalTooltip("highlight-settings");
}

onMounted(() => {
  isScaleDegree.value = store.isScaleDegree;
  registerTooltip("highlight-settings", hideColumnTooltip);
});

onUnmounted(() => {
  unregisterTooltip("highlight-settings");
});
</script>

<style scoped lang="scss">
.highlighting-settings {
  border-radius: 8px;
  transition: all 0.36s ease-out;
}

/* Collapsed State Styling */
.highlighting-settings--collapsed {
  /* Hide labels and collapse width */
  .labels-column {
    opacity: 0;
    transform: translateY(-8px);
    pointer-events: none;
    width: 0;
    overflow: hidden;
  }

  /* Fade out and shrink color and bright buttons */
  .color-button,
  .dim-button {
    opacity: 0;
    transform: translateY(-12px) scale(0.8);
    pointer-events: none;
    width: 32px;
    height: 32px;
  }

  /* Shrink show buttons but keep them visible and functional */
  .show-button {
    width: 32px;
    height: 32px;
    border-width: 2px;

    .button-note-label {
      font-size: 12px;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      .note-label {
        font-size: 10px !important;
        width: 18px !important;
        height: 18px !important;

        svg {
          width: 18px !important;
          height: 18px !important;
          font-size: 10px !important;
        }

        text {
          font-size: 10px !important;
        }
      }

      .scale-degree-label {
        font-size: 10px !important;
        width: 18px !important;
        height: 18px !important;

        svg {
          width: 12px !important;
          height: 12px !important;
          font-size: 8px !important;
        }

        text {
          font-size: 8px !important;
        }

        .degree-number {
          font-size: 10px !important;
        }

        /* Override size classes */
        &.scale-degree-label--medium {
          .degree-number {
            font-size: 10px !important;
          }

          svg {
            width: 12px !important;
            height: 12px !important;
          }
        }

        &.scale-degree-label--small {
          .degree-number {
            font-size: 8px !important;
          }

          svg {
            width: 10px !important;
            height: 10px !important;
          }
        }
      }
    }
  }

  /* Reduce gaps in collapsed state */
  .highlight-settings-layout {
    gap: 0;
  }

  .controls-columns {
    gap: 6px;
  }

  .note-column {
    gap: 6px;
  }
}

.highlight-settings-layout {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  transition: all 0.36s ease-out;
}

.labels-column {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px; /* Match gap in note-column */
  margin-top: 0; /* Align with first button row */
  transition: all 0.36s ease-out;
}

.function-label {
  /* Match existing row labels from FretboardSettings */
  height: 48px; /* Match button height */
  text-align: left;
  color: var(--shade-70);
  font-size: 12px;
  display: flex;
  align-items: center;

  flex: none;
  order: 0;
  flex-grow: 0;
}

.controls-columns {
  display: flex;
  gap: 8px;
  justify-content: center;
  transition: all 0.36s ease-out;
}

.note-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  transition: all 0.36s ease-out;
}

button {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease, outline 0.1s ease-out;
}

.show-button {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 4px solid var(--shade-10);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.36s ease-out, outline 0.1s ease-out;
  background-color: var(--shade-10);

  svg {
    width: 48px;
    height: 100%;
  }

  // State-based classes for show buttons
  &.show-button--hidden {
    // Styles for hidden notes
    background-color: var(--shade-10);
  }
}

.color-button,
.dim-button {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 1;
  transform: scale(1);
  transition: all 0.36s ease-out, outline 0.1s ease-out;
  background-color: var(--shade-10);
  border: 4px solid var(--shade-10);

  svg {
    width: 24px;
    height: 24px;
  }

  // New state-based classes for color buttons
  &.color-button--active {
    background-color: var(--shade-30) !important;
  }
  &.color-button--inactive {
    background-color: var(--shade-10) !important;
  }
  &.color-button--hidden {
    opacity: 0 !important;
    transform: scale(0.1) !important;
    pointer-events: none;
  }

  // New state-based classes for dim buttons
  &.dim-button--bright {
    // Styles when note is bright
    background-color: var(--shade-30) !important;
  }
  &.dim-button--dimmed {
    // Styles when note is dimmed
    background-color: var(--shade-10) !important;
  }
  &.dim-button--hidden {
    opacity: 0 !important;
    transform: scale(0.1) !important;
    pointer-events: none;
  }
}
// NoteLabel styling within show buttons
.button-note-label {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

/* Responsive Design for Highlight Settings */

/* Medium screens: Reduce gap */
@media (max-width: 900px) {
  .highlight-settings-layout {
    gap: 20px;
  }

  .controls-columns {
    gap: 6px;
  }
}

/* Small screens: Hide labels, reduce button sizes and gaps */
@media (max-width: 640px) {
  .labels-column {
    display: none; /* Hide function labels on smaller screens */
  }

  .highlight-settings-layout {
    gap: 16px;
  }

  .controls-columns {
    gap: 4px;
  }

  .note-column {
    gap: 6px;
  }

  button {
    width: 40px;
    height: 40px;
  }

  .show-button {
    width: 40px;
    height: 40px;
    border-width: 3px;
  }

  .color-button,
  .dim-button {
    width: 40px;
    height: 40px;
    border-width: 3px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
}

/* Extra small screens: Further reduce sizes (labels remain hidden) */
@media (max-width: 480px) {
  .labels-column {
    display: none; /* Ensure labels stay hidden */
  }

  .highlight-settings-layout {
    gap: 12px;
  }

  .controls-columns {
    gap: 3px;
  }

  .note-column {
    gap: 4px;
  }

  button {
    width: 36px;
    height: 36px;
  }

  .show-button {
    width: 36px;
    height: 36px;
    border-width: 2px;
  }

  .color-button,
  .dim-button {
    width: 36px;
    height: 36px;
    border-width: 2px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
}
</style>
